import { useState } from "react";
import { MOCK_GOALS } from "../data/mockData";
import { GOALS_CONFIG } from "../data/goalsData";

/**
 * useGoals
 *
 * Manages the editable monthly targets (from GOALS_CONFIG) and
 * the list of dynamic custom goals the user can add/remove at runtime.
 *
 * Returns:
 *   targets       – current saved targets { [targetKey]: number }
 *   draft         – in-progress edits while the edit form is open
 *   setDraft      – update draft fields
 *   editing       – boolean
 *   setEditing    – open/close the edit form
 *   saved         – boolean, true briefly after saving
 *   handleSave    – commit draft → targets
 *   extraGoals    – array of custom goal objects
 *   addGoal       – fn({ label, target, unit }) → adds to extraGoals
 *   removeGoal    – fn(id) → removes from extraGoals
 *   goalsData     – raw MOCK_GOALS (read-only actuals)
 */
export function useGoals() {
  // Build initial targets from GOALS_CONFIG keys + MOCK_GOALS values
  const initialTargets = Object.fromEntries(
    GOALS_CONFIG.metrics.map(m => [m.targetKey, MOCK_GOALS[m.targetKey] ?? 0])
  );

  const [targets,   setTargets]   = useState(initialTargets);
  const [draft,     setDraft]     = useState({ ...initialTargets });
  const [editing,   setEditing]   = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [extraGoals, setExtraGoals] = useState([]);

  function handleSave() {
    setTargets({ ...draft });
    setEditing(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  function addGoal({ label, target, unit }) {
    if (!label.trim() || !target) return;
    setExtraGoals(prev => [
      ...prev,
      { id: Date.now(), label, target: Number(target), unit, current: 0 },
    ]);
  }

  function removeGoal(id) {
    setExtraGoals(prev => prev.filter(g => g.id !== id));
  }

  return {
    targets,
    draft,
    setDraft,
    editing,
    setEditing,
    saved,
    handleSave,
    extraGoals,
    addGoal,
    removeGoal,
    goalsData: MOCK_GOALS,
  };
}